<template>
  <div v-if="visible" class="kep-modal-overlay" @click.self="closeModal">
    <div class="kep-modal-card card">
      <div class="kep-modal-header">
        <h3 class="card-title">{{ title }}</h3>
        <button
          type="button"
          class="close-btn"
          :disabled="busy"
          @click="closeModal"
        >
          ✕
        </button>
      </div>

      <div class="kep-modal-body stack-sm">
        <p class="field-hint">
          Завантажте фото всіх заповнених сторінок по одній або одразу кілька
          (JPG/PNG, до {{ maxFileMb }} МБ кожне, не більше {{ maxPages }} сторінок).
          Порядок фото = порядок сторінок у документі.
        </p>

        <!-- Список сторінок -->
        <div v-if="loadingList" class="field-hint">Завантаження списку сторінок...</div>

        <div v-else-if="pages.length || uploadingItems.length" class="pages-grid">
          <div v-for="(p, i) in pages" :key="p.page" class="page-item">
            <img :src="p.url" :alt="'Сторінка ' + (i + 1)" class="page-thumb">
            <span class="page-num">{{ i + 1 }}</span>
            <button
              type="button"
              class="page-remove"
              title="Видалити"
              :disabled="busy"
              @click="removePage(p)"
            >
              ✕
            </button>
          </div>

          <div v-for="u in uploadingItems" :key="u.id" class="page-item page-item--loading">
            <span class="el-icon-loading"></span>
            <span class="page-loading-text">Завантаження...</span>
          </div>
        </div>

        <div v-else class="pages-empty">
          Фото ще не додано
        </div>

        <!-- Вибір файлів -->
        <label class="field">
          <span class="field-label">Додати фото <span class="req">*</span></span>
          <input
            ref="fileInput"
            type="file"
            class="input-file"
            accept="image/jpeg,image/png"
            multiple
            :disabled="busy || pages.length >= maxPages"
            @change="handleFilesSelect"
          />
          <span v-if="pages.length >= maxPages" class="field-hint">
            Досягнуто ліміт у {{ maxPages }} сторінок
          </span>
        </label>

        <span v-if="errorText" class="field-error">{{ errorText }}</span>
      </div>

      <div class="kep-modal-footer">
        <button
          type="button"
          class="btn btn-ghost"
          :disabled="busy"
          @click="closeModal"
        >
          Скасувати
        </button>
        <button
          type="button"
          class="btn btn-primary"
          :disabled="!pages.length || busy"
          @click="onConfirm"
        >
          <span v-if="confirming" class="el-icon-loading" style="margin-right: 6px;"></span>
          {{ confirming ? 'Відправлення...' : 'Підтвердити' + (pages.length ? ' (' + pages.length + ')' : '') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>

import {
  getOnboardingDocumentPages,
  uploadOnboardingDocumentPage,
  confirmOnboardingDocumentPages, deleteOnboardingDocumentPage

} from '@/api/onboarding';

export default {
  name: 'PassportPagesModal',
  props: {
    visible: { type: Boolean, default: false },
    docKey: { type: String, required: true },
    docLabel: { type: String, default: '' },
    maxPages: { type: Number, default: 10 },
    maxFileMb: { type: Number, default: 15 },
  },
  data() {
    return {
      pages: [],
      uploadingItems: [],
      loadingList: false,
      confirming: false,
      errorText: '',
    };
  },
  computed: {
    title() {
      return this.docLabel ? `Завантаження: ${this.docLabel}` : 'Завантаження сторінок документа';
    },
    busy() {
      return this.uploadingItems.length > 0 || this.confirming;
    },
    baseUrl() {
      return `/api/onboarding/documents/${encodeURIComponent(this.docKey)}/pages`;
    },
  },
  watch: {
    visible: {
      immediate: true,
      handler(val) {
        if (val) {
          this.errorText = '';
          this.loadPages();
        }
      },
    },
  },
  methods: {
    async loadPages() {
      this.loadingList = true;
      try {

        const { data } = await getOnboardingDocumentPages(this.docKey);
        this.pages = data.pages || [];
      } catch (e) {
        console.error('Помилка завантаження сторінок:', e);
        this.pages = [];
      } finally {
        this.loadingList = false;
      }
    },

    async handleFilesSelect(event) {
      const files = Array.from(event.target.files || []);
      event.target.value = '';
      if (!files.length) return;

      this.errorText = '';
      const free = this.maxPages - this.pages.length;
      if (files.length > free) {
        this.errorText = `Можна додати ще лише ${free} фото`;
        files.length = free;
      }

      for (const file of files) {
        if (!['image/jpeg', 'image/png'].includes(file.type)) {
          this.errorText = `«${file.name}»: підтримуються лише JPG та PNG`;
          continue;
        }
        if (file.size > this.maxFileMb * 1024 * 1024) {
          this.errorText = `«${file.name}» завелике (максимум ${this.maxFileMb} МБ)`;
          continue;
        }
        await this.uploadOne(file);
      }
    },

    async uploadOne(file) {
      const item = { id: `${Date.now()}_${Math.random()}` };
      this.uploadingItems.push(item);

      try {
        const formData = new FormData();
        formData.append('file', file);

        const { data } = await uploadOnboardingDocumentPage(this.docKey, formData);
        this.pages.push({ page: data.page, url: data.url });

      } catch (e) {
        console.error('Помилка завантаження фото:', e);
        this.errorText = e.response?.data?.message || `Не вдалося завантажити «${file.name}»`;
      } finally {
        this.uploadingItems = this.uploadingItems.filter(u => u.id !== item.id);
      }
    },

    async removePage(p) {
      if (this.busy) return;
      try {

        await deleteOnboardingDocumentPage(this.docKey, encodeURIComponent(p.page));

        this.pages = this.pages.filter(x => x.page !== p.page);
      } catch (e) {
        this.errorText = 'Не вдалося видалити фото';
      }
    },

    async onConfirm() {
      if (!this.pages.length || this.busy) return;

      this.confirming = true;
      this.errorText = '';
      try {

        const params = {
          name_ua: this.docLabel
        }

        await confirmOnboardingDocumentPages(this.docKey, params);

        this.$emit('confirmed', { docKey: this.docKey });
        this.pages = [];
        this.$emit('update:visible', false);
      } catch (e) {
        console.error('Помилка підтвердження:', e);
        this.errorText = e.response?.data?.message || 'Не вдалося відправити фото на обробку';
      } finally {
        this.confirming = false;
      }
    },

    closeModal() {
      if (this.busy) return;
      this.$emit('update:visible', false);
      this.$emit('close');
    },
  },
};
</script>

<style scoped>
.kep-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 16px;
}

.kep-modal-card {
  background: #ffffff;
  border-radius: 12px;
  max-width: 560px;
  width: 100%;
  max-height: calc(100vh - 32px);
  overflow-y: auto;
  padding: 24px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

.kep-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.close-btn {
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: #64748b;
}

.close-btn:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.input-file {
  display: block;
  width: 100%;
  padding: 8px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  margin-top: 6px;
}

.pages-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 10px;
}

.page-item {
  position: relative;
  aspect-ratio: 3 / 4;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  background: #f4f6f8;
  overflow: hidden;
}

.page-item--loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: #64748b;
  font-size: 12px;
}

.page-thumb {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.page-num {
  position: absolute;
  left: 6px;
  bottom: 6px;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  border-radius: 11px;
  background: rgba(15, 23, 42, 0.7);
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  line-height: 22px;
  text-align: center;
}

.page-remove {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.92);
  color: #f56c6c;
  font-size: 12px;
  font-weight: bold;
  cursor: pointer;
  line-height: 24px;
  padding: 0;
}

.page-remove:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.pages-empty {
  padding: 24px 12px;
  border: 2px dashed #cbd5e1;
  border-radius: 8px;
  background: #f8fafc;
  text-align: center;
  color: #94a3b8;
  font-size: 14px;
}

.field-error {
  color: #ef4444;
  font-size: 12.5px;
  font-weight: 500;
  display: block;
}

.kep-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
}

@media (max-width: 768px) {
  .kep-modal-card {
    padding: 16px;
  }
  .pages-grid {
    grid-template-columns: repeat(auto-fill, minmax(84px, 1fr));
  }
}
</style>
